/**
 * Mob textures at their art's aspect (2026-10-31; 2026-10-30 systems review, item 1). Many bestiary
 * generators draw a `size`x`size` texture over a non-square viewBox, so the art letterboxes inside
 * the square. MobRenderer then stretches the whole texture to the hitbox, and the art draws at its
 * own aspect times the hitbox's: squeezed whenever the hitbox is not square. Fitting the texture to
 * its viewBox here, once for every mob, makes the drawn shape the hitbox's shape, which
 * `tests/contracts/mob-art-fit.test.ts` holds to the art. A texture already at its viewBox aspect,
 * or one that opts out of aspect with `preserveAspectRatio="none"`, is left unchanged.
 */
import { svgFromBase64, svgToBase64 } from '../svg/SVGUtils';

const DATA_URL_PREFIX = 'data:image/svg+xml;base64,';
/** Texture and viewBox aspects this close are treated as equal (rounding in the generators). */
const SAME_ASPECT = 0.02;

/** The svg markup with its width and height set to its viewBox aspect, keeping the longer side. */
export function fitSvgToViewBox(svg: string): string {
  const head = /<svg\b[^>]*>/.exec(svg)?.[0];
  if (!head || /\bpreserveAspectRatio="none"/.test(head)) return svg;
  const viewBox = /\bviewBox="[-\d.]+[ ,]+[-\d.]+[ ,]+([\d.]+)[ ,]+([\d.]+)"/.exec(head);
  const width = /\swidth="([\d.]+)"/.exec(head);
  const height = /\sheight="([\d.]+)"/.exec(head);
  if (!viewBox || !width || !height) return svg;
  const aspect = Number(viewBox[1]) / Number(viewBox[2]);
  const w = Number(width[1]), h = Number(height[1]);
  if (!(aspect > 0) || !(w > 0) || !(h > 0) || Math.abs(w / h / aspect - 1) <= SAME_ASPECT) return svg;
  const side = Math.max(w, h);
  const [fittedW, fittedH] = aspect >= 1 ? [side, Math.round(side / aspect)] : [Math.round(side * aspect), side];
  const fittedHead = head
    .replace(/\swidth="[\d.]+"/, ` width="${fittedW}"`)
    .replace(/\sheight="[\d.]+"/, ` height="${fittedH}"`);
  return svg.replace(head, fittedHead);
}

/** {@link fitSvgToViewBox} for a base64 SVG data URL, the form generators return. */
export function fitMobTexture(dataUrl: string): string {
  if (!dataUrl.startsWith(DATA_URL_PREFIX)) return dataUrl;
  const svg = svgFromBase64(dataUrl);
  const fitted = fitSvgToViewBox(svg);
  return fitted === svg ? dataUrl : svgToBase64(fitted);
}
